/**
 * @file audioCacheStore.js
 * @description Owns the browser-side audio bucket and the small ledger that gives it a
 *              lifecycle: a byte cap, oldest-first eviction, and an owner, so audio cached by
 *              listening neither grows without limit nor outlives the session that heard it.
 *              Audio somebody DOWNLOADED is the exception, and it is pinned to say so: it is
 *              theirs until they remove it, and no arriving identity takes it away.
 *
 * Two stores, one bucket. The bytes live in Cache Storage; the bookkeeping lives in IndexedDB,
 * because Cache Storage cannot answer "how big is this" or "which is oldest" without reading
 * every body back, and answering both cheaply is the ledger's whole job.
 *
 * A ledger row is `{ key, bytes, lastPlayedAt, pins }`, one per cache entry, keyed by the same
 * logical request URL the bucket is keyed by — there is one key scheme here, not two. `pins` is
 * a refcount: an entry with `pins > 0` was asked for deliberately, so the cap below neither
 * evicts it nor counts its bytes. An offline download is what raises one (see the pin section at
 * the foot of this file); the download's own, larger budget is counted from the pinned rows.
 *
 * The owner is one more row in the same store, holding the uid whose session filled the bucket.
 * It carries no `lastPlayedAt`, so the index eviction walks skips it for free.
 *
 * Writes go through this module rather than straight to Cache Storage, which is what lets the
 * two rules below hold: nothing is cached that the ledger cannot account for, and nothing
 * fetched under an identity can land after that identity is gone.
 */

const AUDIO_CACHE_NAME = 'orbiters-audio-assets-v1';

/**
 * How much recently-played audio the bucket may hold. Deliberately smaller than the cap an
 * explicit download gets: this is the incidental residue of listening, not something anyone asked
 * to keep, and it must never be the reason a device runs short of room.
 */
export const AUDIO_PLAY_CACHE_MAX_BYTES = 1024 * 1024 * 1024;

const LEDGER_DB_NAME = 'orbiters-audio-cache';
const LEDGER_DB_VERSION = 1;
const LEDGER_STORE = 'entries';
const LAST_PLAYED_INDEX = 'lastPlayedAt';
const OWNER_ROW_KEY = 'orbiters:audio-cache:owner';

let ledgerDbPromise = null;

/**
 * The ledger opened neither successfully nor with a refusal — it simply never answered.
 *
 * This is kept apart from "IndexedDB is unavailable" because the two demand opposite responses. An
 * unavailable ledger means the bytes cannot be accounted for OR attributed, so they do not stay
 * (see {@link reconcileAudioCacheOwner}). A STALLED one means we could not ask — and deleting
 * someone's whole downloaded library because one open was slow would be a far worse answer than
 * the question deserved.
 *
 * So a stall neither deletes nor trusts: the bucket is left exactly as it is, and no read or write
 * touches it for the rest of the session. Nothing is lost, and no audio is served under an
 * identity nobody could verify.
 */
let ledgerStalled = false;

/**
 * Bumped every time the bucket is emptied. A fetch authorized under one identity can still be
 * in flight when that identity goes; the generation it started under is what tells the write
 * afterwards that its bytes are no longer allowed here.
 */
let cacheGeneration = 0;

/**
 * Every mutation runs through one queue. Two tracks finishing at once would otherwise each plan
 * an eviction from the same pre-eviction totals and between them cut far more than the cap asks,
 * and a clear interleaved with a write could leave either half applied.
 */
let ledgerQueue = Promise.resolve();

function serializeLedgerWork(work) {
  const settled = ledgerQueue.then(work, work);
  ledgerQueue = settled.then(
    () => undefined,
    () => undefined,
  );
  return settled;
}

/** The generation a fetch is starting under; hand it back to {@link writeAudioCacheEntry}. */
export function currentAudioCacheGeneration() {
  return cacheGeneration;
}

function getIndexedDbFactory() {
  return typeof globalThis !== 'undefined' && globalThis.indexedDB ? globalThis.indexedDB : null;
}

/**
 * Delete the ledger database and open it fresh, so the upgrade that builds its store runs again.
 * @returns {Promise<IDBDatabase|null>}
 */
async function rebuildLedger() {
  const factory = getIndexedDbFactory();
  if (!factory) return null;

  const deleted = await new Promise((resolve) => {
    let request;
    try {
      request = factory.deleteDatabase(LEDGER_DB_NAME);
    } catch {
      resolve(false);
      return;
    }
    const timer = setTimeout(() => resolve(false), LEDGER_OPEN_TIMEOUT_MS);
    const settle = (value) => {
      clearTimeout(timer);
      resolve(value);
    };
    request.onsuccess = () => settle(true);
    request.onerror = () => settle(false);
    request.onblocked = () => settle(false);
  });
  if (!deleted) return null;

  return new Promise((resolve) => {
    let request;
    try {
      request = factory.open(LEDGER_DB_NAME, LEDGER_DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LEDGER_STORE)) {
        const store = db.createObjectStore(LEDGER_STORE, { keyPath: 'key' });
        store.createIndex(LAST_PLAYED_INDEX, LAST_PLAYED_INDEX);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

/**
 * How long an open may take before it is treated as a refusal.
 *
 * An IndexedDB open does not always answer. A request left stuck in the backend for this origin —
 * a delete that never completed, a connection nothing released — leaves every later open queued
 * behind it, firing neither success nor error nor `blocked`. The boot AWAITS this store's
 * reconcile, so a queue like that stopped `initializeApp` outright: the shell rendered, no session
 * ever loaded, and nothing was logged. Bounding it turns that into a session with no audio cache
 * lifecycle, which is a degraded feature rather than an app that will not start.
 */
const LEDGER_OPEN_TIMEOUT_MS = 3000;

/**
 * Opens the ledger, or resolves null where IndexedDB is unavailable, refuses to open (private
 * windows, denied storage) or does not answer at all. Only a SUCCESSFUL open is remembered: a
 * transient refusal must not disable the lifecycle for the rest of the page, which would quietly
 * turn the store back into the unbounded, unclearable one this exists to replace.
 * @returns {Promise<IDBDatabase|null>}
 */
function openLedger() {
  const factory = getIndexedDbFactory();
  if (!factory) return Promise.resolve(null);
  // A ledger that has already stalled will stall again, and every caller that tried paid the full
  // timeout to learn it — seconds each, and one console line each, on a store that is asked
  // several times per refresh. Once is the answer for the whole session.
  if (ledgerStalled) return Promise.resolve(null);
  if (ledgerDbPromise) return ledgerDbPromise;

  const pending = new Promise((resolve) => {
    let settled = false;
    let upgrading = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => {
      if (upgrading) return; // never abandon an upgrade — see `onupgradeneeded` below
      ledgerStalled = true;
      // Said once, here, because this is the only path that gives up for the whole session. The
      // other ways of resolving null are per-call refusals and are not worth a line each.
      console.warn(
        '[AudioCacheStore] The cache ledger did not open — audio caching is off for this session.',
      );
      settle(null);
    }, LEDGER_OPEN_TIMEOUT_MS);
    const finish = (value) => {
      clearTimeout(timer);
      settle(value);
    };

    let request;
    try {
      request = factory.open(LEDGER_DB_NAME, LEDGER_DB_VERSION);
    } catch {
      finish(null);
      return;
    }

    request.onupgradeneeded = () => {
      // Once an upgrade starts it must finish. Abandoning one leaves the database CREATED at its
      // new version with its store half-made; every later open then sees the version it wants,
      // never runs the upgrade again, and every transaction fails for good. So the timeout above
      // stands down from here.
      upgrading = true;
      const db = request.result;
      if (!db.objectStoreNames.contains(LEDGER_STORE)) {
        const store = db.createObjectStore(LEDGER_STORE, { keyPath: 'key' });
        store.createIndex(LAST_PLAYED_INDEX, LAST_PLAYED_INDEX);
      }
    };
    request.onsuccess = () => {
      // A timed-out open can still succeed later. Its connection would then be live and untracked —
      // and an untracked connection is exactly what blocks the next upgrade or delete, so a store
      // that timed out once would make itself progressively harder to open.
      if (settled) {
        try {
          request.result.close();
        } catch {
          // Nothing more to do; it was never handed out.
        }
        return;
      }
      finish(request.result);
    };
    request.onerror = () => finish(null);
    request.onblocked = () => finish(null);
  });

  ledgerDbPromise = pending.then(async (opened) => {
    // A database that opens but is MISSING its store is broken, not empty: its version is already
    // current, so the upgrade that would build the store never runs again. Delete and rebuild —
    // nothing is lost that was not already unreachable, and the alternative is a store that can
    // never work in this profile again.
    let db = opened;
    if (db && !db.objectStoreNames.contains(LEDGER_STORE)) {
      console.warn('[AudioCacheStore] The cache ledger was missing its store — rebuilding it.');
      try {
        db.close();
      } catch {
        // Best effort; the delete below reports whether it worked.
      }
      db = await rebuildLedger();
    }
    if (!db) ledgerDbPromise = null;
    return db;
  });

  return ledgerDbPromise;
}

/**
 * Runs `work(store)` inside one transaction and settles on the transaction, not on the last
 * request — so a caller that awaits this knows the writes are durable. `work` must chain IDB
 * requests by callback and never await anything outside them; a transaction that yields to the
 * event loop is closed by the time the next request is issued.
 * @param {IDBDatabase} db
 * @param {IDBTransactionMode} mode
 * @param {(store: IDBObjectStore, done: (value: any) => void) => void} work
 * @returns {Promise<any>}
 */
function runLedgerTransaction(db, mode, work) {
  return new Promise((resolve, reject) => {
    let tx;
    try {
      tx = db.transaction(LEDGER_STORE, mode);
    } catch (error) {
      reject(error);
      return;
    }

    let outcome;
    tx.oncomplete = () => resolve(outcome);
    tx.onerror = () => reject(tx.error || new Error('Audio cache ledger transaction failed.'));
    tx.onabort = () => reject(tx.error || new Error('Audio cache ledger transaction aborted.'));

    try {
      work(tx.objectStore(LEDGER_STORE), (value) => {
        outcome = value;
      });
    } catch (error) {
      try {
        tx.abort();
      } catch {
        // The transaction is already going down; the abort handler settles the promise.
      }
      reject(error);
    }
  });
}

/**
 * The bucket, with no waiting. For use INSIDE the owner reconcile itself, which cannot wait for
 * its own result.
 */
async function openBucketRaw() {
  // A stalled ledger means nothing can be accounted for or attributed this session, so the bucket
  // is closed to everyone rather than half-used: no reads, no writes, and above all no deletes.
  if (ledgerStalled) return null;
  if (typeof caches === 'undefined' || typeof caches.open !== 'function') {
    return null;
  }
  return caches.open(AUDIO_CACHE_NAME);
}

/**
 * Resolves once the owner has been reconciled — i.e. once it is known whose bytes these are.
 *
 * Boot used to AWAIT the reconcile, which put an IndexedDB open on the path to the app appearing;
 * where that open was slow, every load paid the wait before a single orbiter began. The safety rule
 * was never "reconcile before the app starts" though — it is "reconcile before anything reads or
 * writes these bytes". So the wait moved here, to the one door every read and write goes through,
 * and boot no longer waits at all.
 */
let ownerReconciled = Promise.resolve();

/**
 * One resolver per armed gate, oldest first — NOT a single mutable one. Two reconciles can be
 * armed before either finishes (an embed swapping host tokens), and with one resolver the first
 * to finish would open the second's gate, letting a read reach bytes whose owner has not been
 * settled yet. See the same note in `offlineLibraryStore`.
 */
const pendingReconcileGates = [];

/** Declare that a reconcile is about to run. Bucket access after this waits for it. */
export function expectAudioCacheReconcile() {
  ownerReconciled = new Promise((resolve) => {
    pendingReconcileGates.push(resolve);
  });
}

/** Release the oldest gate still waiting. A finish with none armed has nothing to open. */
function markOwnerReconciled() {
  pendingReconcileGates.shift()?.();
}

/**
 * The bucket, once its owner is known. Bounded, because a gate that never opens would be a worse
 * failure than the stale read it prevents — by the ceiling below the reconcile has either happened
 * or cannot.
 */
async function openAudioBucket() {
  await Promise.race([
    ownerReconciled,
    new Promise((resolve) => setTimeout(resolve, LEDGER_OPEN_TIMEOUT_MS + 500)),
  ]);
  return openBucketRaw();
}

function normalizeBytes(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : 0;
}

/**
 * Strictly increasing play stamps. `Date.now()` alone repeats within a millisecond, and two
 * entries sharing a stamp fall back to primary-key order in the index — which is not play order,
 * so the cap could discard the newer of the two.
 */
let lastStamp = 0;
function nextPlayStamp() {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

/**
 * Walks the entries oldest-first and names the ones to drop: unpinned only, and only until the
 * unpinned total is back under the cap. Pinned bytes are not counted — the download cap is a
 * separate, larger budget, and letting a download's bytes push against this cap would have a
 * pinned collection quietly evict everything the listener had played.
 */
function planEviction(store, maxBytes, done) {
  const unpinned = [];
  let unpinnedBytes = 0;

  const cursorRequest = store.index(LAST_PLAYED_INDEX).openCursor();
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (cursor) {
      const row = cursor.value;
      if (!Number(row?.pins)) {
        const bytes = normalizeBytes(row?.bytes);
        unpinned.push({ key: row.key, bytes });
        unpinnedBytes += bytes;
      }
      cursor.continue();
      return;
    }

    const doomed = [];
    let index = 0;
    while (unpinnedBytes > maxBytes && index < unpinned.length) {
      doomed.push(unpinned[index].key);
      unpinnedBytes -= unpinned[index].bytes;
      index += 1;
    }
    done(doomed);
  };
}

/**
 * Deletes bytes from the bucket and reports which keys actually went. A key that would not
 * delete keeps its ledger row: dropping the row anyway would leave the bytes cached but
 * untracked, which is exactly the unbounded state this store exists to prevent.
 * @returns {Promise<string[]>}
 */
async function dropFromBucket(keys) {
  if (!keys.length) return [];
  const bucket = await openBucketRaw();
  if (!bucket) return [];

  const deleted = [];
  for (const key of keys) {
    try {
      await bucket.delete(key);
      deleted.push(key);
    } catch {
      // Left in the ledger, so the next eviction reconsiders it.
    }
  }
  return deleted;
}

/**
 * Evicts down to the cap: plan inside one transaction, clear the bucket, then drop the rows the
 * bucket actually gave up.
 * @returns {Promise<string[]>} the keys evicted
 */
async function enforceCap(db, maxBytes) {
  const doomed = await runLedgerTransaction(db, 'readonly', (store, done) => {
    planEviction(store, maxBytes, done);
  });
  if (!doomed?.length) return [];

  const deleted = await dropFromBucket(doomed);
  if (!deleted.length) return [];

  await runLedgerTransaction(db, 'readwrite', (store, done) => {
    for (const key of deleted) {
      store.delete(key);
    }
    done(deleted);
  });
  return deleted;
}

function putLedgerRow(store, key, bytes, done) {
  const existingRequest = store.get(key);
  existingRequest.onsuccess = () => {
    const existing = existingRequest.result;
    store.put({
      key,
      bytes: normalizeBytes(bytes) || normalizeBytes(existing?.bytes),
      lastPlayedAt: nextPlayStamp(),
      pins: Number(existing?.pins) || 0,
    });
    done(undefined);
  };
}

/**
 * Records that `key` is in the bucket and was just played, then brings the bucket back under the
 * cap by dropping the least recently played unpinned entries. Safe to call on a cache hit as well
 * as a fresh write — a hit IS a play, and only bumping on writes would make "oldest" mean "first
 * fetched" and evict the track someone replays most.
 * @param {string|null} key logical cache request URL
 * @param {number} bytes size of the cached body
 * @param {{ maxBytes?: number }} [options]
 * @returns {Promise<string[]>} the keys evicted, if any
 */
export async function noteAudioCachePlay(key, bytes, { maxBytes = AUDIO_PLAY_CACHE_MAX_BYTES } = {}) {
  if (!key) return [];

  return serializeLedgerWork(async () => {
    const db = await openLedger();
    if (!db) return [];

    await runLedgerTransaction(db, 'readwrite', (store, done) => {
      putLedgerRow(store, key, bytes, done);
    });

    return enforceCap(db, maxBytes);
  });
}

/**
 * Puts a response in the bucket and ledgers it, as one step.
 *
 * Two rules live here rather than at the call site. Without a ledger nothing is cached at all:
 * a store that cannot be measured cannot be capped and cannot be proven to belong to anyone, and
 * silently filling one is the behaviour this module replaces. And a write whose generation is
 * stale is refused — a fetch authorized under an identity that has since gone would otherwise
 * finish afterwards and quietly restore bytes that logging out had just removed.
 * @param {string|null} key logical cache request URL
 * @param {Response} response the response to store (already cloned by the caller)
 * @param {number} bytes size of the body
 * @param {number} generation from {@link currentAudioCacheGeneration}, taken before the fetch
 * @returns {Promise<boolean>} whether the bytes were stored
 */
export async function writeAudioCacheEntry(key, response, bytes, generation) {
  if (!key || !response) return false;

  return serializeLedgerWork(async () => {
    if (generation !== cacheGeneration) return false;

    const db = await openLedger();
    if (!db) return false;

    const bucket = await openBucketRaw();
    if (!bucket) return false;

    await bucket.put(key, response);
    await runLedgerTransaction(db, 'readwrite', (store, done) => {
      putLedgerRow(store, key, bytes, done);
    });

    await enforceCap(db, AUDIO_PLAY_CACHE_MAX_BYTES);
    return true;
  });
}

/**
 * Makes the ledger and the bucket agree.
 *
 * Both directions matter, and the first one is why this exists at all: every browser that has
 * already played anything is carrying bucket entries written before there was a ledger, and an
 * entry with no row is one the cap can neither see nor evict — the unbounded store would simply
 * survive underneath the fix. Adoption is cheap because a cached response still carries the
 * `content-length` it was fetched with, so a size costs a header read, not a body read. Anything
 * that will not state its size is dropped instead: bytes that cannot be budgeted cannot be
 * bounded, and the cost of dropping one is a single refetch.
 *
 * Adopted entries are stamped as older than any real play, so they are the first to go.
 * @returns {Promise<{ adopted: number, dropped: number }>}
 */
async function adoptUntrackedBucketEntries(db) {
  const bucket = await openBucketRaw();
  if (!bucket) return { adopted: 0, dropped: 0 };

  const requests = await bucket.keys();
  const rowKeys = await runLedgerTransaction(db, 'readonly', (store, done) => {
    const request = store.getAllKeys();
    request.onsuccess = () => done(new Set(request.result));
  });

  const adopt = [];
  let dropped = 0;
  for (const request of requests) {
    if (rowKeys.has(request.url)) continue;
    let bytes = 0;
    try {
      const response = await bucket.match(request);
      bytes = normalizeBytes(response?.headers?.get('content-length'));
    } catch {
      bytes = 0;
    }
    if (bytes) {
      adopt.push({ key: request.url, bytes });
    } else {
      try {
        await bucket.delete(request);
        dropped += 1;
      } catch {
        // Leave it; the next boot tries again.
      }
    }
  }

  // A row whose bytes are gone would hold budget against nothing, and would let the cap evict
  // a real entry in its place. This is also what repairs a crash between the two deletes.
  const stale = [...rowKeys].filter(
    (key) => key !== OWNER_ROW_KEY && !requests.some((request) => request.url === key),
  );

  if (adopt.length || stale.length) {
    await runLedgerTransaction(db, 'readwrite', (store, done) => {
      for (const { key, bytes } of adopt) {
        store.put({ key, bytes, lastPlayedAt: 1, pins: 0 });
      }
      for (const key of stale) {
        store.delete(key);
      }
      done(undefined);
    });
  }

  return { adopted: adopt.length, dropped };
}

async function emptyBucketAndLedger() {
  cacheGeneration += 1;

  if (typeof caches !== 'undefined' && typeof caches.delete === 'function') {
    try {
      await caches.delete(AUDIO_CACHE_NAME);
    } catch {
      // Nothing else can be done about a bucket that refuses to go; still clear the ledger.
    }
  }

  const db = await openLedger();
  if (!db) return;
  await runLedgerTransaction(db, 'readwrite', (store, done) => {
    store.clear();
    done(undefined);
  });
}

/**
 * Empties the bucket and every ledger row, owner included — pinned entries too.
 *
 * This is the whole-bucket erase, and a change of identity is NOT a reason to call it: bytes
 * somebody deliberately downloaded belong to them until they remove them, and outliving their
 * session is the point of having downloaded them. What a departing identity takes with it is the
 * incidental residue of listening, which is {@link clearUnpinnedAudioCache}.
 * @returns {Promise<void>}
 */
export async function clearAudioCache() {
  return serializeLedgerWork(emptyBucketAndLedger);
}

/**
 * The unpinned half of the bucket, gone: bytes first, then the rows that owned them.
 *
 * The two halves answer to different rules. A play-cache entry is a side effect of listening: it
 * may be dropped at any time, and an identity leaving is as good a moment as any, because nobody
 * chose it and nothing is lost but a refetch. A PINNED entry was chosen — it is a downloaded
 * collection's bytes — and it stays until its owner removes it, which is the rule this whole
 * feature is built on. Bucket entries with no ledger row are unpinned by definition (a pin lives
 * on a row), so they go with the rest.
 *
 * A row whose bytes would not delete KEEPS its row, exactly as the cap's eviction does — dropping
 * it anyway would leave bytes cached that nothing accounts for, which is the unbounded state this
 * store exists to prevent. The generation is bumped for the same reason a full clear bumps it: a
 * fetch authorized under the identity that is leaving must not land afterwards.
 */
async function dropUnpinnedEntries(db) {
  cacheGeneration += 1;

  const pinnedKeys = await runLedgerTransaction(db, 'readonly', (store, done) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const pinned = new Set();
      for (const row of request.result || []) {
        if (row?.key !== OWNER_ROW_KEY && Number(row?.pins) > 0) pinned.add(row.key);
      }
      done(pinned);
    };
  });

  const bucket = await openBucketRaw();
  const bucketKeys = new Set(bucket ? (await bucket.keys()).map((request) => request.url) : []);
  const doomed = [...bucketKeys].filter((key) => !pinnedKeys.has(key));
  const deleted = new Set(await dropFromBucket(doomed));

  await runLedgerTransaction(db, 'readwrite', (store, done) => {
    const request = store.getAll();
    request.onsuccess = () => {
      for (const row of request.result || []) {
        if (row?.key === OWNER_ROW_KEY || pinnedKeys.has(row?.key)) continue;
        // Either its bytes went, or it never had any here.
        if (deleted.has(row.key) || !bucketKeys.has(row.key)) store.delete(row.key);
      }
      done(undefined);
    };
  });

  return deleted.size;
}

/**
 * Reconciles the bucket against whoever is actually here. The standalone app is never told that
 * someone logged out — the session cookie is httpOnly and there is no cross-origin channel — so
 * boot is the only honest moment to notice, and noticing at boot is enough: the bytes are
 * unreadable to the app until it next runs. The embed has a real signal instead, and calls this
 * when the host hands over or withdraws a token.
 *
 * Any change of identity drops the UNPINNED half, in either direction, anonymous included. A
 * signed-out visitor arriving where a uid owned the bucket is the case that matters (a shared
 * browser); the reverse costs one refetch. The owner row is therefore always written, even for an
 * anonymous visitor — leaving it out would let the next account inherit that visitor's bucket
 * unchallenged.
 *
 * What it does NOT do is touch a pinned entry. Those bytes are a downloaded collection, and a
 * download belongs to the person who asked for it until they remove it — a browser two people
 * share holds both their libraries side by side rather than one destroying the other's on the way
 * in. Which manifests each of them may SEE is the library store's business (`offlineLibraryStore`);
 * this store only makes sure the bytes are still there to see.
 *
 * When nothing changed, the bucket is instead brought under the cap — which on the first boot
 * after this shipped means adopting whatever was already cached, and evicting down to the cap if
 * that turns out to be more than it allows.
 * @param {string|null} uid the viewer Firebase auth resolved, or null when nobody is signed in
 * @param {{ maxBytes?: number }} [options]
 * @returns {Promise<boolean>} whether the identity changed
 */
export async function reconcileAudioCacheOwner(
  uid,
  { maxBytes = AUDIO_PLAY_CACHE_MAX_BYTES, identityEstablished = true } = {},
) {
  const viewerUid = uid || null;

  return serializeLedgerWork(async () => {
    // An identity nobody could establish is not a change of identity. An offline boot cannot ask
    // the server who is here, and reading that silence as "somebody else" would empty the library
    // of the very person who downloaded it, on the first boot where having it mattered. So an
    // unestablished identity leaves both the bytes and the owner row exactly as they are — the same
    // posture a stalled ledger already takes, and for the same reason: no evidence is not evidence.
    if (!identityEstablished) return false;

    const db = await openLedger();
    if (!db) {
      // No ledger, no pins — and with no pins there is no way to tell a downloaded collection from
      // the residue of listening. Dropping the lot would take somebody's library with it, which is
      // the one thing this must never do; nothing is written through this store while it is in
      // this state anyway, so the bucket can neither grow nor be added to. It is left alone.
      // (A STALL is the same posture for a different reason — see `ledgerStalled`.)
      return false;
    }

    const owner = await runLedgerTransaction(db, 'readonly', (store, done) => {
      const request = store.get(OWNER_ROW_KEY);
      request.onsuccess = () => {
        const row = request.result;
        done({ known: Boolean(row), uid: row?.uid || null });
      };
    });

    const changed = owner.known && owner.uid !== viewerUid;
    if (changed) {
      // The play cache goes; the downloads stay. See the note above.
      await dropUnpinnedEntries(db);
    } else {
      await adoptUntrackedBucketEntries(db);
      await enforceCap(db, maxBytes);
    }

    await runLedgerTransaction(db, 'readwrite', (store, done) => {
      store.put({ key: OWNER_ROW_KEY, uid: viewerUid });
      done(undefined);
    });

    return changed;
  }).finally(() => {
    // Released whatever the outcome: a reconcile that failed must not leave every bucket access
    // waiting on a gate nothing will open.
    markOwnerReconciled();
  });
}

// ── Pins ─────────────────────────────────────────────────────────────────────────────────────────
// A pin is what turns an incidental cache entry into a kept one. `pins` was already the field the
// cap refuses to evict; everything below is the vocabulary for setting it, and it is deliberately
// the ONLY refcount in the system. The offline library records WHICH collection asked for a key;
// how many askers a key currently has is answered here and nowhere else, so the two can never
// disagree about whether bytes may go.
//
// Pins are only ever taken on bytes that are actually present. A row with no bytes behind it is
// reaped by the boot reconcile as stale, and a pin living on such a row would be silently lost —
// so `pinCacheEntries` reports what it could not pin rather than pretending.

/**
 * Raise the refcount on entries that are already in the bucket.
 * @param {string[]} keys logical cache request URLs
 * @returns {Promise<{ pinned: string[], missing: string[] }>} `missing` = no bytes, so no pin taken
 */
export async function pinCacheEntries(keys) {
  const wanted = [...new Set((keys || []).filter(Boolean))];
  if (!wanted.length) return { pinned: [], missing: [] };

  return serializeLedgerWork(async () => {
    const db = await openLedger();
    if (!db) return { pinned: [], missing: wanted };

    return runLedgerTransaction(db, 'readwrite', (store, done) => {
      const pinned = [];
      const missing = [];
      let index = 0;

      const step = () => {
        if (index >= wanted.length) {
          done({ pinned, missing });
          return;
        }
        const key = wanted[index];
        index += 1;
        const request = store.get(key);
        request.onsuccess = () => {
          const row = request.result;
          if (!row) {
            missing.push(key);
          } else {
            store.put({ ...row, pins: (Number(row.pins) || 0) + 1 });
            pinned.push(key);
          }
          step();
        };
      };

      step();
    });
  });
}

/**
 * Lower the refcount, and report the keys that reached zero — those are back under the play cap's
 * authority, which is what makes a removal free its bytes without deleting anything a second
 * collection is still holding. Nothing is deleted here: an unpinned entry is a recently-played one,
 * and the cap decides when it goes.
 * @param {string[]} keys
 * @returns {Promise<string[]>} the keys whose last pin was released
 */
export async function unpinCacheEntries(keys) {
  const wanted = [...new Set((keys || []).filter(Boolean))];
  if (!wanted.length) return [];

  return serializeLedgerWork(async () => {
    const db = await openLedger();
    if (!db) return [];

    return runLedgerTransaction(db, 'readwrite', (store, done) => {
      const released = [];
      let index = 0;

      const step = () => {
        if (index >= wanted.length) {
          done(released);
          return;
        }
        const key = wanted[index];
        index += 1;
        const request = store.get(key);
        request.onsuccess = () => {
          const row = request.result;
          const pins = Number(row?.pins) || 0;
          if (row && pins > 0) {
            const next = pins - 1;
            store.put({ ...row, pins: next });
            if (next === 0) released.push(key);
          }
          step();
        };
      };

      step();
    });
  });
}

/**
 * Put bytes in the bucket already pinned, as one step.
 *
 * The same two rules as {@link writeAudioCacheEntry} — no ledger row, no bytes; a stale generation
 * is refused — plus one this write adds: the entry is born with a pin, so the cap can never take it
 * between the put and a separate pin call. Deliberately does NOT enforce the play cap afterwards:
 * pinned bytes are not counted by it, and a download evicting the listener's recently-played audio
 * on its way past would be the surprise the separate budgets exist to prevent.
 *
 * `takePin: false` is for a caller REPLACING bytes it already holds a pin on — a downloaded file
 * the browser reclaimed and the collection is refetching. Taking another pin there would raise the
 * refcount above the number of things actually holding the entry, and since a removal releases one
 * pin per holder, the entry could never come back to zero: the bytes would be unfreeable. The write
 * still leaves the entry pinned at least once, because the caller does hold one.
 *
 * @param {string} key logical cache request URL
 * @param {Response} response already cloned by the caller
 * @param {number} bytes
 * @param {number} generation from {@link currentAudioCacheGeneration}, taken before the fetch
 * @param {{ takePin?: boolean }} [options]
 * @returns {Promise<boolean>} whether the bytes were stored
 */
export async function writePinnedCacheEntry(key, response, bytes, generation, { takePin = true } = {}) {
  if (!key || !response) return false;

  return serializeLedgerWork(async () => {
    if (generation !== cacheGeneration) return false;

    const db = await openLedger();
    if (!db) return false;

    const bucket = await openBucketRaw();
    if (!bucket) return false;

    await bucket.put(key, response);
    await runLedgerTransaction(db, 'readwrite', (store, done) => {
      const existingRequest = store.get(key);
      existingRequest.onsuccess = () => {
        const existing = existingRequest.result;
        const current = Number(existing?.pins) || 0;
        store.put({
          key,
          bytes: normalizeBytes(bytes) || normalizeBytes(existing?.bytes),
          lastPlayedAt: nextPlayStamp(),
          pins: takePin ? current + 1 : Math.max(current, 1),
        });
        done(undefined);
      };
    });

    return true;
  });
}

/**
 * Which of these keys have bytes in the bucket right now.
 *
 * Asked of the bucket, never of the ledger: a manifest can outlive its bytes (the browser may
 * reclaim a whole origin's storage under pressure), and an entry reported as downloaded on the
 * strength of a row alone would fail silently at the moment it was needed. This is the check that
 * makes "available offline" mean it, and the one that decides what a resumed download refetches.
 * @param {string[]} keys
 * @returns {Promise<Set<string>>} the subset actually present
 */
export async function readPresentCacheKeys(keys) {
  const wanted = new Set((keys || []).filter(Boolean));
  if (!wanted.size) return new Set();

  const bucket = await openAudioBucket();
  if (!bucket) return new Set();

  // ONE enumeration, intersected — not a `match` per key. A `match` reads an entry, and a manifest
  // of fifty files asked fifty times over is enough round trips to be felt; a library of ten such
  // collections, re-checked whenever anything changed, was enough to stall the page.
  let cached;
  try {
    cached = await bucket.keys();
  } catch {
    // A bucket that will not enumerate is treated as empty: everything reads as missing, which
    // costs refetches and never a false "available offline".
    return new Set();
  }

  const present = new Set();
  for (const request of cached) {
    const url = typeof request === 'string' ? request : request?.url;
    if (url && wanted.has(url)) present.add(url);
  }
  return present;
}

/**
 * Every pinned key with its size — what a download actually occupies, and the snapshot the byte
 * budget and the "play this from local bytes" decision are both read from. Shared keys appear once,
 * because they are stored once.
 * @returns {Promise<Map<string, number>>} key → bytes
 */
export async function readPinnedCacheEntries() {
  return serializeLedgerWork(async () => {
    const db = await openLedger();
    if (!db) return new Map();

    return runLedgerTransaction(db, 'readonly', (store, done) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const pinned = new Map();
        for (const row of request.result || []) {
          if (row?.key && Number(row.pins) > 0) {
            pinned.set(row.key, normalizeBytes(row.bytes));
          }
        }
        done(pinned);
      };
    });
  });
}

export { AUDIO_CACHE_NAME, openAudioBucket };
